import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { getDeviceSecurity } from '@/api/deviceSecurity';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import {
  filterRows, filtersFromParams, levelHint, levelLabel, noFilters, summarise, switchText,
  type DeviceSecurityFilters,
} from '@/lib/deviceSecurity';
import type { DefenderLevel, DeviceSecurityRow } from '@/types/oscar';

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal';
const PAGE = 200;

const levelCls: Record<DefenderLevel, string> = {
  full: 'bg-navy/10 text-navy',
  partial: 'bg-orange/15 text-navy',
  off: 'bg-orange/15 text-navy',
  notreported: 'bg-darkGrey/10 text-darkGrey',
};

function LevelBadge({ row }: { row: DeviceSecurityRow }) {
  return (
    <span
      title={row.DefenderHealthReasons ?? levelHint[row.DefenderLevel]}
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${levelCls[row.DefenderLevel]}`}
    >
      {levelLabel[row.DefenderLevel]}
    </span>
  );
}

function CrowdStrikeBadge({ row }: { row: DeviceSecurityRow }) {
  if (row.CrowdStrike === 'present') {
    return (
      <span title={row.CrowdStrikeApp ?? undefined} className="inline-flex flex-col">
        <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-orange/15 px-2 py-0.5 text-xs font-semibold text-navy">Still installed</span>
        {row.CrowdStrikeVersion && <span className="mt-0.5 text-xs text-darkGrey/70">v{row.CrowdStrikeVersion}</span>}
      </span>
    );
  }
  if (row.CrowdStrike === 'absent') {
    return (
      <span title={row.CrowdStrikeLastSeen ? `Last listed ${formatDate(row.CrowdStrikeLastSeen)}` : 'Intune does not list CrowdStrike on this device'} className="inline-flex flex-col">
        <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-navy/10 px-2 py-0.5 text-xs font-semibold text-navy">Not listed</span>
        {row.CrowdStrikeLastSeen && <span className="mt-0.5 text-xs text-darkGrey/70">was on {formatDate(row.CrowdStrikeLastSeen)}</span>}
      </span>
    );
  }
  return <span title="The CrowdStrike check has not covered this device yet" className="inline-flex whitespace-nowrap rounded-full bg-darkGrey/10 px-2 py-0.5 text-xs font-semibold text-darkGrey">Not checked</span>;
}

function Tile({ label, value, hint, onClick, active }: { label: string; value: number; hint: string; onClick: () => void; active: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={hint}
      className={`rounded border p-3 text-left shadow-sm ${active ? 'border-navy bg-navy/5' : 'border-border bg-white hover:bg-bg'}`}
    >
      <span className="block text-2xl font-bold text-navy">{value}</span>
      <span className="block text-xs font-semibold text-darkGrey">{label}</span>
    </button>
  );
}

/**
 * Device security (Anthony Bolack, 04/11/2026): Onsite is moving from CrowdStrike to Microsoft Defender, and CrowdStrike
 * limits what Defender can do, so the team needs to see, for each Windows device, what level of Defender is on and
 * whether CrowdStrike is still installed.
 *
 * <p>⚠ BOTH HALVES ARE INTUNE'S VIEW and refresh when a device checks in: the Defender level from its protection state,
 * CrowdStrike from its discovered apps. "Not listed" is not an uninstall certificate, and the page says so. There is no
 * Defender risk score here (that is the Defender portal).</p>
 */
export function DeviceSecurity() {
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<DeviceSecurityFilters>(() => filtersFromParams(params));
  const [limit, setLimit] = useState(PAGE);

  const query = useQuery({ queryKey: ['device-security'], queryFn: ({ signal }) => getDeviceSecurity(signal) });

  const rows = useMemo(() => query.data?.Rows ?? [], [query.data]);
  const summary = useMemo(() => summarise(rows), [rows]);
  const shown = useMemo(() => filterRows(rows, filters), [rows, filters]);

  const set = (patch: Partial<DeviceSecurityFilters>) => { setFilters((f) => ({ ...f, ...patch })); setLimit(PAGE); };
  const only = (patch: Partial<DeviceSecurityFilters>) => { setFilters({ ...noFilters, search: filters.search, ...patch }); setLimit(PAGE); };

  const csNeverRan = query.data && !query.data.CrowdStrikeSynced;
  const defenderNeverRan = query.data && !query.data.DefenderSynced;

  return (
    <section>
      <header className="mb-5">
        <Link to="/assets" className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-darkGrey hover:text-navy">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Assets
        </Link>
        <h1 className="font-heading text-2xl font-bold text-navy">Device security</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          Windows devices enrolled in Intune: what level of Microsoft Defender is switched on, and whether CrowdStrike is still
          installed. While CrowdStrike is on a device it can limit what Defender does.
        </p>
      </header>

      {csNeverRan && (
        <p role="status" className="mb-3 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
          <span>The CrowdStrike check has not run yet, so every device shows <strong>Not checked</strong>. An admin can run it from Admin &gt; Sync jobs.</span>
        </p>
      )}
      {defenderNeverRan && (
        <p role="status" className="mb-3 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
          <span>The Defender health sync has not run yet, so every device shows <strong>Not reported</strong>.</span>
        </p>
      )}

      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Tile label="Windows devices" value={summary.total} hint="Show everything" onClick={() => only({})} active={!filters.crowdStrike && !filters.level && !filters.checkIn && !filters.needsLook} />
        <Tile label="CrowdStrike still installed" value={summary.crowdStrikePresent} hint="Intune lists a CrowdStrike app on these" onClick={() => only({ crowdStrike: 'present' })} active={filters.crowdStrike === 'present'} />
        <Tile label="Defender full" value={summary.full} hint={levelHint.full} onClick={() => only({ level: 'full' })} active={filters.level === 'full'} />
        <Tile label="Worth a look" value={summary.needsLook} hint="No CrowdStrike listed and Defender off or not reported" onClick={() => only({ needsLook: true })} active={filters.needsLook} />
      </div>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          CrowdStrike
          <select aria-label="Filter by CrowdStrike" value={filters.crowdStrike} onChange={(e) => set({ crowdStrike: e.target.value as DeviceSecurityFilters['crowdStrike'] })} className={inputCls}>
            <option value="">All</option>
            <option value="present">Still installed</option>
            <option value="absent">Not listed</option>
            <option value="unchecked">Not checked</option>
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Defender level
          <select aria-label="Filter by Defender level" value={filters.level} onChange={(e) => set({ level: e.target.value as DeviceSecurityFilters['level'] })} className={inputCls}>
            <option value="">All</option>
            <option value="full">Full ({summary.full})</option>
            <option value="partial">Partial ({summary.partial})</option>
            <option value="off">Off ({summary.off})</option>
            <option value="notreported">Not reported ({summary.notReported})</option>
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Last check-in
          <select aria-label="Filter by last check-in" value={filters.checkIn} onChange={(e) => set({ checkIn: e.target.value as DeviceSecurityFilters['checkIn'] })} className={inputCls}>
            <option value="">Any time</option>
            <option value="recent">Within 7 days</option>
            <option value="over7">More than 7 days ago</option>
            <option value="over30">More than 30 days ago</option>
            <option value="never">No check-in recorded</option>
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input aria-label="Search devices" value={filters.search} onChange={(e) => set({ search: e.target.value })} placeholder="Hostname, serial, model or person" className={inputCls} />
        </label>
      </div>

      <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <th className="px-3 py-2">Device</th>
              <th className="px-3 py-2">Assigned to</th>
              <th className="px-3 py-2">Defender</th>
              <th className="px-3 py-2" title="Malware protection">Malware</th>
              <th className="px-3 py-2" title="Real-time protection">Real-time</th>
              <th className="px-3 py-2" title="Network inspection">Network</th>
              <th className="px-3 py-2" title="Tamper protection">Tamper</th>
              <th className="px-3 py-2">CrowdStrike</th>
              <th className="px-3 py-2">Last check-in</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && <tr><td colSpan={9} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>}
            {query.isError && <tr><td colSpan={9} className="px-3 py-6 text-center text-red">Could not load device security.</td></tr>}
            {query.data && shown.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-darkGrey/60">No devices match.</td></tr>}
            {shown.slice(0, limit).map((r) => (
              <tr key={r.AssetId} className="border-t border-border hover:bg-bg/60">
                <td className="px-3 py-2 align-top">
                  <Link to={`/assets/${r.AssetId}`} className="font-semibold text-navy hover:underline">{r.Hostname ?? `Asset ${r.AssetId}`}</Link>
                  <div className="text-xs text-darkGrey/70">{[r.Model, r.SerialNumber].filter(Boolean).join(' · ') || '—'}</div>
                </td>
                <td className="px-3 py-2 align-top text-darkGrey/80">{r.AssignedTo ?? '—'}</td>
                <td className="px-3 py-2 align-top"><LevelBadge row={r} /></td>
                <td className="px-3 py-2 align-top text-darkGrey/80">{switchText(r.MalwareProtectionEnabled)}</td>
                <td className="px-3 py-2 align-top text-darkGrey/80">{switchText(r.RealTimeProtectionEnabled)}</td>
                <td className="px-3 py-2 align-top text-darkGrey/80">{switchText(r.NetworkInspectionEnabled)}</td>
                <td className="px-3 py-2 align-top text-darkGrey/80">{switchText(r.TamperProtectionEnabled)}</td>
                <td className="px-3 py-2 align-top"><CrowdStrikeBadge row={r} /></td>
                <td className="px-3 py-2 align-top text-darkGrey/80">{formatDate(r.IntuneLastCheckIn)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {shown.length > limit && (
        <button type="button" onClick={() => setLimit((l) => l + PAGE)} className="mt-3 text-sm font-semibold text-navy hover:underline">
          Show more ({shown.length - limit} not shown)
        </button>
      )}

      <p className="mt-4 text-xs text-darkGrey/70">
        Showing {Math.min(limit, shown.length)} of {shown.length}.
        {' '}Both columns are Intune&rsquo;s view and refresh when a device checks in: Defender at {query.data?.DefenderSynced ? formatDateTime(query.data.DefenderSynced) : 'never'},
        {' '}CrowdStrike at {query.data?.CrowdStrikeSynced ? formatDateTime(query.data.CrowdStrikeSynced) : 'never'}.
        {' '}&ldquo;Not listed&rdquo; means Intune did not list a CrowdStrike app at that check, not that it is certainly gone.
        {' '}A Defender level of Off can be Defender running passive beside another antivirus. There is no Defender risk score here: that is in the Defender portal.
      </p>
    </section>
  );
}
